import {
  AccountType,
  AddOn_Create,
  AddOn_Read,
  AddOn_Read_Nested,
  AddOn_Read_Typeahead,
  AddOn_Update,
  AddOnCharge_Create,
  AddOnCharge_Read,
  AddOnCharge_Update_Destroyable,
  AddOnChargeSource,
  AddOnRateType,
  AddOnType,
  AutoAddOnAssignment_Read,
  AutoAddOnAssignmentSourceType,
  BillingState,
  Destroyable,
  FuelSurchargeScheduleType,
  RejectedReason,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { CompanyBasic } from '~hooks/useCompany/models';
import {
  FuelSurchargeScheduleBasic,
  FuelSurchargeScheduleTypeahead,
} from '~hooks/useFuelSurchargeSchedule/model';
import { JobBasic } from '~hooks/useJob/models';
import { SettlementBasic } from '~hooks/useSettlements/models';
import {
  AddOnChargeDTO,
  removeAddOnRateTypeFromName,
} from '~pages/Approvals/DriverPay/helpers';
import { AddOnDTO } from '~pages/Settings/RatesManagement/AddOnCharges/components/addOnFormSchema';
import { Nullable } from '~types/Nullable';
import { toDecimal } from '~utils/decimal';

export class AddOn {
  constructor(
    private _id: Nullable<string>,
    private _name: string,
    private _revenue_cost_rate_type: AddOnRateType,
    private _add_on_types: Array<AddOnType>,
    private _quantity: number,
    // `rate`/`costRateValue` are null when the current user lacks the `view_rate`
    // permission and the backend omits the value.
    private _rate: Nullable<number>,
    private _external_id: Nullable<string>,
    private _percentage: number,
    private _company: CompanyBasic,
    private _accounting_id: Nullable<string>,
    private _cost_rate_value: Nullable<number>,
    private _taxable: boolean,
    private _fuel_surcharge_schedule?: FuelSurchargeScheduleTypeahead,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get revenueCostRateType() {
    return this._revenue_cost_rate_type;
  }

  public set revenueCostRateType(revenueCostRateType: AddOnRateType) {
    this._revenue_cost_rate_type = revenueCostRateType;
  }

  public get addOnTypes() {
    return this._add_on_types;
  }

  public get quantity() {
    return this._quantity;
  }

  public set quantity(quantity: number) {
    this._quantity = quantity;
  }

  public set percentage(percentage: number) {
    this._percentage = percentage;
  }

  public get percentage() {
    return this._percentage;
  }

  public get rate() {
    return this._rate;
  }

  public get externalId() {
    return this._external_id;
  }

  public get company() {
    return this._company;
  }

  public get accountingId() {
    return this._accounting_id;
  }

  public get costRateValue() {
    return this._cost_rate_value;
  }

  public set costRateValue(costRateValue: Nullable<number>) {
    this._cost_rate_value = costRateValue;
  }

  public get taxable() {
    return this._taxable;
  }

  public get fuelSurchargeSchedule() {
    return this._fuel_surcharge_schedule;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static deparse(proto: AddOnDTO) {
    const data: AddOn_Create = {
      name: proto.name,
      revenue_cost_rate_type: proto.revenueCostRateType.id as AddOnRateType,
      add_on_types: proto.addOnTypes?.map((i) => i.id as AddOnType) || [],
      quantity: proto.quantity ?? undefined,
      ...(proto.revenueCostRateType.id === AddOnRateType.RATE_PERCENT_OF_TOTAL
        ? { percentage: proto.rate ?? undefined }
        : { revenue_rate_value: proto.rate ?? undefined }),
      external_id: proto.externalId || null,
      accounting_id: proto.accountingId || null,
      cost_rate_value: proto.costRateValue || null,
      taxable: proto.taxable ?? false,
      fuel_surcharge_schedule_id:
        proto.revenueCostRateType.id === AddOnRateType.FUEL_SURCHARGE
          ? proto.fuelSurchargeSchedule?.id
          : null,
    };

    return data;
  }

  public static deparseUpdate(proto: AddOnDTO) {
    const data: AddOn_Update = {
      name: proto.name,
      revenue_cost_rate_type: proto.revenueCostRateType.id as AddOnRateType,
      add_on_types: proto.addOnTypes?.map((i) => i.id as AddOnType) || [],
      quantity: proto.quantity ?? undefined,
      ...(proto.revenueCostRateType.id === AddOnRateType.RATE_PERCENT_OF_TOTAL
        ? { percentage: proto.rate ?? undefined }
        : { revenue_rate_value: proto.rate ?? undefined }),
      external_id: proto.externalId || null,
      accounting_id: proto.accountingId || null,
      cost_rate_value: proto.costRateValue || null,
      taxable: proto.taxable ?? false,
      fuel_surcharge_schedule_id:
        proto.revenueCostRateType.id === AddOnRateType.FUEL_SURCHARGE
          ? proto.fuelSurchargeSchedule?.id
          : null,
    };

    return data;
  }

  public static parse(proto: AddOn_Read) {
    return new AddOn(
      proto.id ?? '',
      proto.name,
      proto.revenue_cost_rate_type,
      proto.add_on_types,
      Number(proto.quantity),
      isNil(proto.revenue_rate_value) ? null : Number(proto.revenue_rate_value),
      proto.external_id,
      Number(proto.percentage),
      CompanyBasic.parse(proto.company),
      proto.accounting_id ?? null,
      isNil(proto.cost_rate_value) ? null : Number(proto.cost_rate_value),
      proto.taxable ?? false,
      proto.fuel_surcharge_schedule
        ? FuelSurchargeScheduleTypeahead.parse(proto.fuel_surcharge_schedule)
        : undefined,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class AddOnBasic {
  constructor(
    private _id: string,
    private _name: string,
    private _add_on_rate_type: AddOnRateType,
    private _quantity: number,
    // Null when the current user lacks the `view_rate` permission and the backend
    // omits the value.
    private _rate: Nullable<number>,
    private _percentage: number,
    private _fuel_surcharge_schedule?: Nullable<FuelSurchargeScheduleBasic>,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get addOnRateType() {
    return this._add_on_rate_type;
  }

  public get quantity() {
    return this._quantity;
  }

  public get rate() {
    return this._rate;
  }

  public get percentage() {
    return this._percentage;
  }

  public get fuelSurchargeSchedule() {
    return this._fuel_surcharge_schedule;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: AddOn_Read_Nested) {
    return new AddOnBasic(
      proto.id,
      proto.name,
      proto.add_on_rate_type,
      Number(proto.quantity),
      isNil(proto.rate) ? null : Number(proto.rate),
      Number(proto.percentage),
      proto.fuel_surcharge_schedule
        ? FuelSurchargeScheduleBasic.parse(proto.fuel_surcharge_schedule)
        : null,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class AddOnTypeahead {
  constructor(
    private _id: string,
    private _name: string,
    private _add_on_rate_type: AddOnRateType,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get addOnRateType() {
    return this._add_on_rate_type;
  }

  public static parse(proto: AddOn_Read_Typeahead) {
    return new AddOnTypeahead(proto.id, proto.name, proto.add_on_rate_type);
  }
}

export class AddOnCharge {
  constructor(
    private _id: Nullable<string>,
    private _name: string,
    private _percentage: number,
    private _quantity: number,
    // Null when the current user lacks the `view_rate` permission and the backend
    // omits the value.
    private _rate: Nullable<number>,
    private _total_amount: number,
    private _revenue_cost_rate_type: AddOnRateType,
    private _external_id: Nullable<string>,
    private _add_on: Nullable<{ id: string }>,
    private _load: Nullable<{ id: string }>,
    private _rebill_revenue_rate_value: Nullable<number>,
    private _rebill_percentage: Nullable<number>,
    private _rebill_total_amount: Nullable<number>,
    private _job: Nullable<JobBasic>,
    private _surcharge_type: Nullable<FuelSurchargeScheduleType> = null,
    private _billing_state: BillingState | null = null,
    private _rejected_reason: RejectedReason | null = null,
    private _manually_edited_at: Dayjs | null = null,
    private _editable: boolean | null = null,
    private _settlement: SettlementBasic | null = null,
    private _derived_total_amount: number | null = null,
    private _source: AddOnChargeSource | null = null,
    private _source_id: string | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get percentage() {
    return this._percentage;
  }

  public get quantity() {
    return this._quantity;
  }

  public get rate() {
    return this._rate;
  }

  public get totalAmount() {
    return this._total_amount;
  }

  public get revenueCostRateType() {
    return this._revenue_cost_rate_type;
  }

  public get externalId() {
    return this._external_id;
  }

  public get addOnId() {
    return this._add_on?.id ?? null;
  }

  public get loadId() {
    return this._load?.id;
  }

  public get rebillRevenueRateValue() {
    return this._rebill_revenue_rate_value;
  }

  public get rebillPercentage() {
    return this._rebill_percentage;
  }

  public get rebillTotalAmount() {
    return this._rebill_total_amount;
  }

  public get job() {
    return this._job;
  }

  public get surchargeType() {
    return this._surcharge_type;
  }

  public get billingState() {
    return this._billing_state;
  }

  public get rejectedReason() {
    return this._rejected_reason;
  }

  public get manuallyEditedAt() {
    return this._manually_edited_at;
  }

  public get editable() {
    return this._editable;
  }

  public get settlement() {
    return this._settlement;
  }

  public get derivedTotalAmount() {
    return this._derived_total_amount;
  }

  public get source() {
    return this._source;
  }

  public get sourceId() {
    return this._source_id;
  }

  public get hasTotalVariance() {
    return (
      this._derived_total_amount !== null &&
      !toDecimal(this._derived_total_amount).equals(toDecimal(this._total_amount))
    );
  }

  public static parse(proto: AddOnCharge_Read) {
    return new AddOnCharge(
      proto.id ?? null,
      proto.name,
      Number(proto.percentage),
      Number(proto.quantity),
      isNil(proto.revenue_rate_value) ? null : Number(proto.revenue_rate_value),
      Number(proto.total_amount),
      proto.revenue_cost_rate_type,
      proto.external_id ?? null,
      proto.add_on,
      proto.load?.id ? { id: proto.load.id } : null,
      isNil(proto.rebill_revenue_rate_value)
        ? null
        : Number(proto.rebill_revenue_rate_value),
      isNil(proto.rebill_percentage) ? null : Number(proto.rebill_percentage),
      isNil(proto.rebill_total_amount) ? null : Number(proto.rebill_total_amount),
      proto.job ? JobBasic.parse(proto.job) : null,
      proto.surcharge_type ?? null,
      proto.billing_state ?? null,
      proto.rejected_reason ?? null,
      proto.manually_edited_at ? dayjs(proto.manually_edited_at) : null,
      proto.editable ?? null,
      proto.settlement ? SettlementBasic.parse(proto.settlement) : null,
      isNil(proto.derived_total_amount) ? null : Number(proto.derived_total_amount),
      proto.source ?? null,
      proto.source_id ?? null,
    );
  }

  public static deparse(proto: AddOnChargeDTO, forRebill: boolean = false) {
    const rateType = proto.addOnRateType as AddOnRateType;
    const isRatePercentOfTotal =
      rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL ||
      (rateType === AddOnRateType.FUEL_SURCHARGE && !!proto.isPercentage);

    if (proto._destroy === 1) {
      if (proto.id) {
        const data: Destroyable = {
          id: proto.id,
          _destroy: 1,
        };
        return data;
      }
      // New item marked for deletion - skip it entirely
      return null;
    }

    const name = removeAddOnRateTypeFromName(proto.addOn.name);

    if (proto.id) {
      const data: AddOnCharge_Update_Destroyable = {
        id: proto.id,
        revenue_cost_rate_type: rateType,
        name,
        percentage: isRatePercentOfTotal ? proto.value : undefined,
        revenue_rate_value: isRatePercentOfTotal ? undefined : proto.value,
        quantity: proto.quantity,
        add_on_id: proto.addOn.id || undefined,
        ...(forRebill &&
          proto.rebillValue !== undefined && {
            rebill_percentage: isRatePercentOfTotal ? proto.rebillValue : undefined,
            rebill_revenue_rate_value: isRatePercentOfTotal
              ? undefined
              : proto.rebillValue,
          }),
      };

      return data;
    }

    const data: AddOnCharge_Create = {
      add_on_id: proto.addOn.id || undefined,
      revenue_cost_rate_type: rateType,
      name,
      percentage: isRatePercentOfTotal ? proto.value : undefined,
      revenue_rate_value: isRatePercentOfTotal ? undefined : proto.value,
      quantity: proto.quantity,
      load_id:
        rateType === AddOnRateType.PER_LOAD && proto.load?.id ? proto.load.id : undefined,
      ...(forRebill &&
        proto.rebillValue !== undefined && {
          rebill_percentage: isRatePercentOfTotal ? proto.rebillValue : undefined,
          rebill_revenue_rate_value: isRatePercentOfTotal ? undefined : proto.rebillValue,
        }),
    };

    return data;
  }
}

export class AddOnAssignment {
  constructor(
    private _id: string,
    private _name: string,
    private _quantity: number,
    // `rate`/`costRateValue` are null when the current user lacks the `view_rate`
    // permission and the backend omits the value.
    private _rate: Nullable<number>,
    private _cost_rate_value: Nullable<number>,
    private _percentage: number,
    private _revenue_cost_rate_type: Nullable<AddOnRateType>,
    private _external_id: Nullable<string>,
    private _accounting_id: Nullable<string>,
    private _total_installments: number,
    private _auto_add_on_installments_count: number,
    private _add_on_types: AddOnType[],
    private _add_on_id: Nullable<string>,
    private _original_auto_add_on_assignment_id: Nullable<string>,
    private _add_on: Nullable<AddOnBasic>,
    private _source_id: Nullable<string>,
    private _source_type: Nullable<AutoAddOnAssignmentSourceType>,
    private _created_at: Dayjs,
    private _updated_at: Dayjs,
    private _account_type: Nullable<AccountType> = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get quantity() {
    return this._quantity;
  }

  public get rate() {
    return this._rate;
  }

  public get costRateValue() {
    return this._cost_rate_value;
  }

  public get percentage() {
    return this._percentage;
  }

  public get revenueCostRateType() {
    return this._revenue_cost_rate_type;
  }

  public get isPercentOfTotal() {
    return this._revenue_cost_rate_type === AddOnRateType.RATE_PERCENT_OF_TOTAL;
  }

  public get isFuelSurcharge() {
    return this._revenue_cost_rate_type === AddOnRateType.FUEL_SURCHARGE;
  }

  public get externalId() {
    return this._external_id;
  }

  public get accountingId() {
    return this._accounting_id;
  }

  public get totalInstallments() {
    return this._total_installments;
  }

  public get autoAddOnInstallmentsCount() {
    return this._auto_add_on_installments_count;
  }

  public get createdAt() {
    return this._created_at;
  }

  public get updatedAt() {
    return this._updated_at;
  }

  public get addOnTypes() {
    return this._add_on_types;
  }

  public get addOnId() {
    return this._add_on_id;
  }

  public get originalAutoAddOnAssignmentId() {
    return this._original_auto_add_on_assignment_id;
  }

  public get addOn() {
    return this._add_on;
  }

  public get fuelSurchargeSchedule() {
    return this._add_on?.fuelSurchargeSchedule;
  }

  public get sourceId() {
    return this._source_id;
  }

  public get sourceType() {
    return this._source_type;
  }

  public get accountType() {
    return this._account_type;
  }

  /**
   * Returns a copy of this assignment with `accountType` overridden. Used when
   * fetching rate-card add-ons (BE returns `account_type: null` for those) and
   * we need to stamp the party based on which rate column we fetched from.
   */
  public withAccountType(accountType: Nullable<AccountType>): AddOnAssignment {
    return new AddOnAssignment(
      this._id,
      this._name,
      this._quantity,
      this._rate,
      this._cost_rate_value,
      this._percentage,
      this._revenue_cost_rate_type,
      this._external_id,
      this._accounting_id,
      this._total_installments,
      this._auto_add_on_installments_count,
      this._add_on_types,
      this._add_on_id,
      this._original_auto_add_on_assignment_id,
      this._add_on,
      this._source_id,
      this._source_type,
      this._created_at,
      this._updated_at,
      accountType,
    );
  }

  public static parse(proto: AutoAddOnAssignment_Read) {
    return new AddOnAssignment(
      proto.id,
      proto.name ?? '',
      Number(proto.quantity),
      isNil(proto.revenue_rate_value) ? null : Number(proto.revenue_rate_value),
      isNil(proto.cost_rate_value) ? null : Number(proto.cost_rate_value),
      Number(proto.percentage),
      proto.revenue_cost_rate_type,
      proto.external_id,
      proto.accounting_id,
      Number(proto.total_installments),
      proto.auto_add_on_installments_count,
      proto.add_on_types,
      proto.add_on_id,
      proto.original_auto_add_on_assignment_id,
      proto.add_on ? AddOnBasic.parse(proto.add_on) : null,
      proto.source_id,
      proto.source_type,
      dayjs(proto.created_at),
      dayjs(proto.updated_at),
      proto.account_type ?? null,
    );
  }
}
